// The piece primitive: one object drawn through its face (package face in SVG, else its v1 template
// through the built-in face, else the default card), wrapped in a target control.

import type { ObjectView } from '../model/table';
import { useEffect, useRef } from 'react';
import { lodForWidth, usePresentation } from '../presentation/PresentationContext';
import { Face } from '../faces/Face';
import { slotValues } from '../faces/resolve';
import type { ThemeV2 } from '../presentation/types';
import { Slot } from '../components/ObjectCard';
import { objectAnchor } from '../play/targets';
import { targetProps, usePlay } from '../play/PlayController';
import { useReplay } from '../scene/ViewContext';

/** The face alone, at a drawn width in CSS pixels. */
const publicBack: ObjectView = { id: 'public-card-back', label: 'Hidden card', title: 'Hidden card', subtitle: null, kind: 'card', zoneId: '', owner: null, faceDown: true, count: null, catalog: null, catalogKey: null, fields: {}, stack: null, details: [], tags: [], at: null, ref: null, points: null, costs: [] };

/** Decorative hidden-count backs never contain a catalog identity or instance reference. */
export function PublicCardBack({ width }: { width: number }) {
  return <PieceFace object={publicBack} zoneKind={null} width={width} />;
}

export function PieceFace({ object, zoneKind, width }: { object: ObjectView; zoneKind: string | null; width: number }) {
  const api = usePresentation();
  const face = api.face(object, zoneKind, lodForWidth(width));
  const theme = (api.presentation?.theme ?? {}) as ThemeV2;
  if (face && !face.legacy) return <Face face={face} theme={theme} />;
  if (object.faceDown) return <span className="piece-back" aria-hidden="true" />;
  const slots = face ? slotValues(face) : {};
  const title = slots.title ?? slots.label;
  const fill = slots.fill?.kind === 'color' ? slots.fill.value : undefined;
  const band = slots.band?.kind === 'color' ? slots.band.value : undefined;
  return (
    <span className="piece-plain" style={{ ...(fill ? { background: fill } : {}), ...(band ? { borderTopColor: band } : {}) } as React.CSSProperties} aria-hidden="true">
      <span className="piece-plain-title">{title?.kind === 'text' ? title.text : object.title}</span>
      {slots.corner && <span className="piece-plain-corner"><Slot value={slots.corner} /></span>}
      {slots.cost && width >= 90 && <span className="piece-plain-cost"><Slot value={slots.cost} /></span>}
      {slots.badge && <span className="piece-plain-badge"><Slot value={slots.badge} /></span>}
      {object.count !== null && object.count > 1 && <span className="piece-plain-count">×{object.count}</span>}
    </span>
  );
}

interface PieceProps {
  object: ObjectView;
  zoneKind: string | null;
  width: number;
  className?: string;
  /** Extra visible text (for example a count badge) drawn over the face. */
  badge?: React.ReactNode;
  style?: React.CSSProperties;
  label?: string;
  /** Tooltip when no target tip applies (for example a compact tile's full rule text). */
  hint?: string;
  /** Objects this piece names (a brick's `linkField`): they light while it is hovered or focused. */
  links?: Set<string> | null;
  /** Declarative public details on mouse hover/keyboard focus; touch uses inspect. */
  hoverDetails?: boolean;
  /** Keep the object in a scene's persistent inspector while hovered or focused. */
  inspectOnHover?: boolean;
  previewPlacement?: 'right';
  /** Appears and leaves in place: the motion layer neither flies it from nor to a seat or zone (it has no motion id), and it pops in instead. */
  still?: boolean;
}

/** A face as a focusable target: anchor `obj:<id>`, motion id, play state classes and the object's label. */
export function Piece({ object, zoneKind, width, className = '', badge, style, label, hint, links, hoverDetails = false, inspectOnHover = false, previewPlacement, still = false }: PieceProps) {
  const play = usePlay();
  const replay = useReplay();
  const anchor = objectAnchor(object.id);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inspectedByHold = useRef(false);
  const holdStart = useRef({ x: 0, y: 0 });
  const clearHold = () => { if (holdTimer.current) clearTimeout(holdTimer.current); holdTimer.current = null; };
  useEffect(() => clearHold, []);
  const changed = replay?.marks?.changed.has(object.id) ? ' is-replay-changed' : '';
  const t = targetProps(play, anchor, label ?? object.label, `piece piece-${object.kind} ${className}${changed}${still ? ' is-still' : ''}`);
  if (hint && !t.title) t.title = hint;
  const st = play?.stateOf(anchor);
  const order = st?.order ?? null;
  const link = links?.size ? { onPointerEnter: () => play?.setLinked(links), onPointerLeave: () => play?.setLinked(null), onFocus: () => play?.setLinked(links), onBlur: () => play?.setLinked(null) } : {};
  return (
    <div {...t} {...link} {...(still ? {} : { 'data-motion-id': object.id })} data-inspect-id={!object.faceDown ? object.id : undefined} data-hover-details={hoverDetails && !object.faceDown ? 'true' : undefined} data-preview-placement={previewPlacement} aria-keyshortcuts="I" style={{ ...style, '--piece-w': `${width}px` } as React.CSSProperties}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); play?.inspect(object.id); }}
      onKeyDown={e => { if (e.key.toLowerCase() === 'i') { e.preventDefault(); e.stopPropagation(); play?.inspect(object.id); } else t.onKeyDown(e); }}
      onFocus={() => { if (inspectOnHover) play?.inspect(object.id); link.onFocus?.(); }}
      onPointerEnter={e => { if (inspectOnHover && e.pointerType !== 'touch') play?.inspect(object.id); link.onPointerEnter?.(); }}
      onPointerDown={e => { clearHold(); if (play?.pending) { e.preventDefault(); return; } inspectedByHold.current = false; holdStart.current = { x: e.clientX, y: e.clientY }; if (e.pointerType === 'touch') holdTimer.current = setTimeout(() => { inspectedByHold.current = true; play?.inspect(object.id); }, 500); }}
      onPointerMove={e => { if (Math.hypot(e.clientX - holdStart.current.x, e.clientY - holdStart.current.y) > 8) clearHold(); }} onPointerCancel={clearHold} onPointerUp={clearHold}
      onClick={e => { if (inspectedByHold.current) { inspectedByHold.current = false; e.preventDefault(); e.stopPropagation(); } else t.onClick(e); }}>
      <PieceFace object={object} zoneKind={zoneKind} width={width} />
      {badge}
      {st?.free && st.selectable && <span className="piece-free" aria-hidden="true">Free</span>}
      {order !== null && <span className="piece-order" aria-hidden="true">{order}</span>}
    </div>
  );
}
